import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import api from '../api';
import { getTeamDisplayName } from '../teamDisplay';

const mainActions = [
  {
    title: 'Add a play',
    description: 'Add the runs, passes, and situations your staff already teaches.',
    to: '/plays/new',
    cta: 'Open add play screen',
  },
  {
    title: 'View saved plays',
    description: 'Review the private playbook Mick OC uses for recommendations.',
    to: '/plays',
    cta: 'Open play list',
  },
  {
    title: 'Review called plays',
    description: 'Look back at the real-time calls recommended from your system.',
    to: '/history',
    cta: 'Open game review',
  },
];

export default function DashboardHome({ user }) {
  const [stats, setStats] = useState({
    plays: '...',
    calls: '...',
  });
  const teamDisplayName = getTeamDisplayName(user);

  useEffect(() => {
    let cancelled = false;

    async function loadOverview() {
      try {
        const [playsRes, historyRes] = await Promise.all([
          api.get(`/api/plays/all/${user?.teamId || 'anything'}`),
          api.get('/api/history'),
        ]);

        if (cancelled) return;

        setStats({
          plays: String(playsRes.data?.plays?.length ?? 0),
          calls: String(historyRes.data?.items?.length ?? 0),
        });
      } catch {
        if (cancelled) return;
        setStats({
          plays: 'Unavailable',
          calls: 'Unavailable',
        });
      }
    }

    loadOverview();

    return () => {
      cancelled = true;
    };
  }, [user?.teamId]);

  return (
    <div className="space-y-5">
      <section className="surface-card">
        <p className="eyebrow">Start Here</p>
        <h2 className="mt-3 text-4xl font-semibold text-brand-black">Enter the playbook Mick OC will use.</h2>
        <p className="mt-3 max-w-3xl text-lg leading-8 text-stone-700">
          Add the plays your staff already teaches, keep them organized, and review the recommendations that came from
          your own offensive system. Use the phone or tablet app when you want a live play call.
        </p>

        <div className="mt-6 grid gap-4 lg:grid-cols-3">
          {mainActions.map((action) => (
            <Link
              key={action.title}
              to={action.to}
              className="rounded-lg border-2 border-stone-200 bg-brand-offwhite px-6 py-6 transition hover:border-brand-orange hover:bg-white"
            >
              <h3 className="text-2xl font-semibold text-brand-black">{action.title}</h3>
              <p className="mt-3 text-base leading-7 text-stone-700">{action.description}</p>
              <span className="mt-5 inline-flex text-base font-semibold text-brand-orange">
                {action.cta}
              </span>
            </Link>
          ))}
        </div>
      </section>

      <section className="grid gap-5 lg:grid-cols-[1.2fr_0.8fr]">
        <div className="surface-card">
          <p className="eyebrow">How To Use It</p>
          <h3 className="mt-3 text-2xl font-semibold text-brand-black">Recommended workflow</h3>
          <ol className="mt-5 space-y-4 text-base text-stone-700">
            <li>1. Use `Add a play` to enter the plays your staff already teaches.</li>
            <li>2. Use `View saved plays` to confirm the private team system is accurate.</li>
            <li>3. Use `Web Play Call` to spot-check real-time recommendations from your playbook.</li>
            <li>4. Use `Review called plays` after games to learn from the recommendations.</li>
          </ol>
        </div>

        <div className="surface-card bg-brand-orange/10">
          <p className="eyebrow">Team Summary</p>
          <h3 className="mt-3 text-2xl font-semibold text-brand-black">
            {teamDisplayName}
          </h3>
          <p className="mt-2 text-base text-stone-700">{user?.schoolName || 'School not listed'}</p>
          <div className="mt-5 space-y-4 text-base text-stone-700">
            <div className="rounded-lg bg-white px-4 py-4">
              <span className="font-semibold text-brand-black">{stats.plays}</span> plays saved
            </div>
            <div className="rounded-lg bg-white px-4 py-4">
              <span className="font-semibold text-brand-black">{stats.calls}</span> calls to review
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
